import * as THREE from "three";

import { Vector2 } from "../../common/vector2.js";
import { Renderer } from "../../core/renderer.js";
import { SceneDimmer } from "../../core/scene_dimmer.js";
import { Game } from "../../game.js";
import { ImgID } from "../../image_loader/img_id.js";
import { ImagesHolder } from "../../image_loader/images_holder.js";
import { BasicEventsController } from "../../touch_events_controller/basic_events_controller.js";
import { ColorScheme } from "../common_ui_elements/color_scheme.js";
import { TextStyleScheme } from "../common_ui_elements/text_style_scheme.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../common_ui_elements/ui_text_element.js";
import {
  BuyBoosterType,
  getBoosterTextVariableParams,
  getDefaultBoosterEffectValue,
} from "../dialog/buy_booster_dialog.js";
import { DialogManager } from "../dialog/dialog_manager.js";
import { UIImage } from "../common_ui_elements/ui_image.js";
import { isMemoryConstrained } from "../../utils/memory_policy.js";

export interface BoosterUIHighlight
{
  canvas: HTMLCanvasElement;
  client_rect: { x: number, y: number, width: number, height: number };
}

export interface BoosterHighlightParams
{
  booster_type: BuyBoosterType;
  effect_value?: number;
  on_pointer_down: ( client_x: number, client_y: number ) => boolean;
  on_pointer_move: ( client_x: number, client_y: number ) => void;
  on_pointer_up: ( client_x: number, client_y: number ) => boolean;
  on_first_shot: () => void;
  on_cancel_booster: () => void;
  on_cancel_aim: () => void;
  on_close: () => void;
  on_scene_dimming: ( active: boolean ) => void;
  on_successful_shot?: () => void;
  aim_tutorial_target?: { client_x: number, client_y: number };
  freeze_share?: () => number;
  ui_highlights: BoosterUIHighlight[];
}

interface BoosterHighlightTextConfig
{
  title_key: string;
  description_key: string;
}

const BOOSTER_TEXT_CONFIG: Record<BuyBoosterType, BoosterHighlightTextConfig> = {
  [BuyBoosterType.Firework]: {
    title_key: "BOOSTER.FIREWORK",
    description_key: "DESCRIPTION.FIREWORK",
  },
  [BuyBoosterType.FiveBalls]: {
    title_key: "BOOSTER.FIVE_BALLS",
    description_key: "DESCRIPTION.FIVE_BALLS",
  },
  [BuyBoosterType.BigBall]: {
    title_key: "BOOSTER.BIG_BALL",
    description_key: "DESCRIPTION.BIG_BALL",
  },
  [BuyBoosterType.InfiniteShots]: {
    title_key: "BOOSTER.INFINITE_SHOTS",
    description_key: "DESCRIPTION.INFINITE_SHOTS",
  },
};

const FREEZE_TEXT_CONFIG: BoosterHighlightTextConfig = {
  title_key: "BOOSTER.FREEZE",
  description_key: "DESCRIPTION.FREEZE",
};

/** Owns the complete booster-highlight presentation, input and lifecycle. */
export class BoosterHighlightRenderer
{
  public static readonly BACKGROUND_OPACITY = 0.78;

  private static readonly TITLE_OUTLINE_COLOR = "#8B048A";
  private static readonly DESCRIPTION_OUTLINE_COLOR = "#000000";
  private static readonly INFINITE_GRADIENT_OPACITY = 0.72;
  private static readonly INFINITE_GRADIENT_HEIGHT = 650;
  private static readonly INFINITE_BAR_W = 736;
  private static readonly INFINITE_BAR_H = 86;
  private static readonly INFINITE_BAR_Y = 320;
  private static readonly INFINITE_BAR_FILL_INSET_X = 11;
  private static readonly INFINITE_BAR_FILL_H = 65;
  private static readonly INFINITE_BAR_PAD = 24;
  private static readonly CURSOR_PULSE_SCALE = 0.8;
  private static readonly CURSOR_PULSE_DURATION_MS = 650;
  private static readonly BOOSTER_BUMP_SCALE = 0.12;
  private static readonly BOOSTER_BUMP_DURATION_MS = 180;

  private static text_canvas: HTMLCanvasElement | null = null;
  private static text_ctx: CanvasRenderingContext2D | null = null;
  private static progress_canvas: HTMLCanvasElement | null = null;
  private static progress_ctx: CanvasRenderingContext2D | null = null;
  private static progress_origin = { x: 0, y: 0, width: 0, height: 0 };
  private static events_controller: BasicEventsController | null = null;

  private static title: UITextElement | null = null;
  private static description: UITextElement | null = null;
  private static standard_description: UITextElement | null = null;
  private static infinite_description: UITextElement | null = null;
  private static progress_bar_rect = { x: 0, y: 0, width: 0, height: 0 };
  private static aim_tutorial_cursor: UIImage | null = null;
  private static aim_cursor_rest_pos: Vector2 | null = null;

  private static params: BoosterHighlightParams | null = null;
  private static pointer_active = false;
  private static countdown_started = false;
  private static duration_started_at = 0;
  private static booster_bump_started_at: number | null = null;
  private static hide_after_booster_bump = false;
  private static closing_bump_timeout: number | null = null;
  private static animation_frame: number | null = null;

  private static ui_dirty = true;
  private static last_game_ui_revision = -1;
  private static last_layout_key = "";
  private static diag = { ui: 0, progress: 0, frames: 0 };

  private static legacyUiEveryFrame(): boolean
  {
    return localStorage.getItem( "perf_bh_text_every_frame" ) === "1";
  }

  public static diagnostics(): Record<string, unknown>
  {
    return {
      shown: this.params !== null,
      scene_dimmer_active: SceneDimmer.isActive(),
      dim_opacity: SceneDimmer.getOpacity(),
      legacy_ui_every_frame: this.legacyUiEveryFrame(),
      countdown: this.countdown_started,
      frames: this.diag.frames,
      ui_repaints: this.diag.ui,
      progress_repaints: this.diag.progress,
    };
  }

  public static resetDiagnostics(): void
  {
    this.diag = { ui: 0, progress: 0, frames: 0 };
  }

  public static markUIDirty(): void
  {
    this.ui_dirty = true;
  }

  private static startBoosterBump( hide_after: boolean ): void
  {
    if ( this.closing_bump_timeout !== null )
    {
      window.clearTimeout( this.closing_bump_timeout );
      this.closing_bump_timeout = null;
    }

    this.booster_bump_started_at = performance.now();
    this.hide_after_booster_bump = hide_after;

    if ( hide_after )
    {
      this.closing_bump_timeout = window.setTimeout( () =>
      {
        this.closing_bump_timeout = null;
        if ( !this.params || !this.hide_after_booster_bump )
          return;

        this.hide();
      }, this.BOOSTER_BUMP_DURATION_MS );
    }
  }

  public static show( params: BoosterHighlightParams ): void
  {
    if ( this.params )
      this.hide();

    this.ensureRenderer();
    this.params = params;
    this.pointer_active = false;
    this.countdown_started = this.isFreeze();
    this.duration_started_at = performance.now();
    this.ui_dirty = true;
    this.last_game_ui_revision = -1;
    this.startBoosterBump( false );
    this.setBoosterText( params.booster_type, params.effect_value );
    this.syncLayout();

    SceneDimmer.disable();
    this.syncDimmingMode();
    this.text_canvas!.style.display = "block";
    this.text_canvas!.style.pointerEvents = "auto";
    this.progress_canvas!.style.display = this.usesProgressBar() ? "block" : "none";
    this.configureAimTutorialCursor();
    this.events_controller!.enable();

    this.renderFrame();
  }

  public static hide(): void
  {
    const params = this.params;
    this.params = null;
    this.pointer_active = false;
    this.booster_bump_started_at = null;
    this.hide_after_booster_bump = false;

    if ( this.closing_bump_timeout !== null )
    {
      window.clearTimeout( this.closing_bump_timeout );
      this.closing_bump_timeout = null;
    }

    if ( this.animation_frame !== null )
    {
      cancelAnimationFrame( this.animation_frame );
      this.animation_frame = null;
    }

    this.events_controller?.disable();
    this.hideAimTutorialCursor();
    if ( this.text_canvas )
    {
      this.text_canvas.style.pointerEvents = "none";
      this.text_canvas.style.display = "none";
      if ( isMemoryConstrained() )
      {
        this.text_canvas.width = 1;
        this.text_canvas.height = 1;
      }
    }
    SceneDimmer.disable();

    if ( this.progress_canvas && this.progress_ctx )
    {
      this.progress_canvas.style.display = "none";
      this.progress_ctx.clearRect( 0, 0, this.progress_origin.width, this.progress_origin.height );
    }

    if ( this.text_ctx )
      this.text_ctx.clearRect( 0, 0, Game.screenW(), Game.screenH() );

    if ( params )
    {
      try
      {
        params.on_cancel_aim();
      }
      finally
      {
        params.on_close();
      }
    }
  }

  public static isShown(): boolean
  {
    return this.params !== null;
  }

  private static ensureRenderer(): void
  {
    if ( this.text_canvas )
      return;

    this.text_canvas = document.createElement( "canvas" );
    this.text_canvas.id = "boosterHighlightTextCanvas";
    this.text_canvas.style.position = "absolute";
    this.text_canvas.style.pointerEvents = "none";
    this.text_canvas.style.touchAction = "none";
    this.text_canvas.style.zIndex = "125";
    this.text_canvas.style.display = "none";
    this.text_ctx = this.text_canvas.getContext( "2d" );
    if ( !this.text_ctx )
      throw new Error( "Could not create the booster-highlight text context" );

    document.body.appendChild( this.text_canvas );

    this.progress_canvas = document.createElement( "canvas" );
    this.progress_canvas.id = "boosterHighlightProgressCanvas";
    this.progress_canvas.style.position = "absolute";
    this.progress_canvas.style.pointerEvents = "none";
    this.progress_canvas.style.zIndex = "126";
    this.progress_canvas.style.display = "none";
    this.progress_ctx = this.progress_canvas.getContext( "2d" );
    if ( !this.progress_ctx )
      throw new Error( "Could not create the booster-highlight progress context" );

    document.body.appendChild( this.progress_canvas );
    this.createTextElements();
    this.createEventsController();
  }

  private static createEventsController(): void
  {
    this.events_controller = new BasicEventsController( this.text_canvas! );
    this.events_controller.init(
      point => this.handlePointerDown( point.x, point.y ),
      point => this.handlePointerUp( point.x, point.y ),
      point => this.handlePointerMove( point.x, point.y )
    );
    this.events_controller.disable();
  }

  private static toClientPoint( local_x: number, local_y: number ): { x: number, y: number }
  {
    const bounds = this.text_canvas!.getBoundingClientRect();
    return { x: bounds.left + local_x, y: bounds.top + local_y };
  }

  private static handlePointerDown( local_x: number, local_y: number ): void
  {
    if ( !this.params )
      return;

    const client = this.toClientPoint( local_x, local_y );
    const inside_booster = this.isPointInsideBoosterHighlight( client.x, client.y );
    if ( inside_booster )
    {
      if ( this.hide_after_booster_bump )
        return;

      if ( !this.countdown_started )
      {
        try
        {
          this.params.on_cancel_booster();
        }
        finally
        {
          this.startBoosterBump( true );
        }
      }
      return;
    }

    this.pointer_active = this.params.on_pointer_down( client.x, client.y );
  }

  private static isPointInsideBoosterHighlight( client_x: number, client_y: number ): boolean
  {
    if ( !this.params )
      return false;

    return this.params.ui_highlights.some( highlight =>
    {
      const rect = highlight.client_rect;
      return client_x >= rect.x
        && client_x <= rect.x + rect.width
        && client_y >= rect.y
        && client_y <= rect.y + rect.height;
    } );
  }

  private static handlePointerMove( local_x: number, local_y: number ): void
  {
    if ( !this.params || !this.pointer_active )
      return;

    const client = this.toClientPoint( local_x, local_y );
    this.params.on_pointer_move( client.x, client.y );
  }

  private static handlePointerUp( local_x: number, local_y: number ): void
  {
    if ( !this.params || !this.pointer_active )
      return;

    this.pointer_active = false;
    const client = this.toClientPoint( local_x, local_y );
    const fired = this.params.on_pointer_up( client.x, client.y );
    if ( !fired )
      return;

    this.params.on_successful_shot?.();

    if ( this.isTimed() )
    {
      if ( !this.countdown_started )
      {
        this.countdown_started = true;
        this.duration_started_at = performance.now();
        this.params.on_first_shot();
        this.syncDimmingMode();
        this.ui_dirty = true;
      }
      return;
    }

    this.hide();
  }

  private static createTextElements(): void
  {
    const ctx = this.text_ctx!;
    this.title = this.createTitleElement( 119 );

    this.standard_description = this.createDescriptionElement( 245, true );
    this.infinite_description = this.createDescriptionElement( 205, false );
    this.description = this.standard_description;

    const bar_w = LayoutScaler.widthFromDesign( this.INFINITE_BAR_W );
    const bar_h = LayoutScaler.heightFromDesign( this.INFINITE_BAR_H );
    const bar_pad = LayoutScaler.heightFromDesign( this.INFINITE_BAR_PAD );
    const bar_x = Math.round( Game.safeAreaLeft() + ( Game.getContentWidth() - bar_w ) / 2 );
    const bar_y = Math.round( Game.safeAreaTop() + LayoutScaler.yFromDesign( this.INFINITE_BAR_Y ) );
    this.progress_origin = {
      x: bar_x - bar_pad,
      y: bar_y - bar_pad,
      width: bar_w + bar_pad * 2,
      height: bar_h + bar_pad * 2,
    };
    this.progress_bar_rect = { x: bar_pad, y: bar_pad, width: bar_w, height: bar_h };

    const cursor_w = LayoutScaler.widthFromDesign( 150 );
    const cursor_h = cursor_w * ( 183 / 159 );
    this.aim_tutorial_cursor = new UIImage(
      ctx,
      0,
      0,
      cursor_w,
      cursor_h,
      ImgID.IconTutorialCursor
    );
    this.aim_tutorial_cursor.setClickable( false );
    this.aim_tutorial_cursor.setOpacityImmediate( 0 );
  }

  private static createTitleElement( design_y: number ): UITextElement
  {
    const title_w = LayoutScaler.widthFromDesign( 1000 );
    const title_h = LayoutScaler.heightFromDesign( 109 );
    const title_x = Game.safeAreaLeft() + ( Game.getContentWidth() - title_w ) / 2;
    const title_y = Game.safeAreaTop() + LayoutScaler.yFromDesign( design_y );
    return new UITextElement(
      this.text_ctx!,
      title_x,
      title_y,
      title_w,
      title_h,
      {
        font_size: LayoutScaler.textSizeFromDesign( 80 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.rubik,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 16 ),
        outline_color: this.TITLE_OUTLINE_COLOR,
        fit_font_size: true,
        min_font_size: LayoutScaler.textSizeFromDesign( 48 ),
        auto_wrap: false,
        ...TextStyleScheme.buttonShadow(),
      }
    );
  }

  private static createDescriptionElement( design_y: number, auto_wrap: boolean ): UITextElement
  {
    const description_w = LayoutScaler.widthFromDesign( 760 );
    return new UITextElement(
      this.text_ctx!,
      Game.safeAreaLeft() + ( Game.getContentWidth() - description_w ) / 2,
      Game.safeAreaTop() + LayoutScaler.yFromDesign( design_y ),
      description_w,
      LayoutScaler.heightFromDesign( 105 ),
      {
        font_size: LayoutScaler.textSizeFromDesign( 46 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.rubik,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        fit_font_size: true,
        min_font_size: LayoutScaler.textSizeFromDesign( 32 ),
        line_height: auto_wrap ? 0.95 : 1,
        auto_wrap,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 6 ),
        outline_color: this.DESCRIPTION_OUTLINE_COLOR,
        ...TextStyleScheme.buttonShadow(),
      }
    );
  }

  private static setBoosterText(
    booster_type: BuyBoosterType,
    effect_value?: number
  ): void
  {
    const config = this.isFreeze() ? FREEZE_TEXT_CONFIG : BOOSTER_TEXT_CONFIG[booster_type];
    const text_params = getBoosterTextVariableParams( booster_type, effect_value );
    this.title!.setTextVariableParams( text_params );
    this.title!.setLocalizationKey( config.title_key );
    this.description = this.isTimed()
      ? this.infinite_description
      : this.standard_description;
    this.description!.setTextVariableParams( text_params );
    this.description!.setLocalizationKey( config.description_key );
  }

  private static getInfiniteShotsDurationMs(): number
  {
    const seconds = this.params?.effect_value
      ?? getDefaultBoosterEffectValue( BuyBoosterType.InfiniteShots )
      ?? 5;
    return Math.max( 0.001, seconds ) * 1000;
  }

  private static renderFrame(): void
  {
    if ( !this.params || !this.text_canvas )
      return;

    if ( DialogManager.isAnyDialogOpened() && !this.isFreeze() )
    {
      this.hide();
      return;
    }

    if ( this.isTimed()
      && this.countdown_started
      && this.getRemainingShare() <= 0 )
    {
      // Render the actual zero state once before closing on the next frame.
      this.syncLayout();
      this.renderUI();
      this.animation_frame = requestAnimationFrame( () => this.hide() );
      return;
    }

    this.diag.frames++;
    this.syncLayout();
    this.syncDimmingMode();
    this.updateBoosterBump( performance.now() );
    if ( this.needsUIRepaint() )
      this.renderUI();
    if ( this.usesProgressBar() )
      this.renderProgressBar();
    this.animation_frame = requestAnimationFrame( () => this.renderFrame() );
  }

  private static needsUIRepaint(): boolean
  {
    if ( this.legacyUiEveryFrame() )
      return true;

    const game_ui_revision = Game.gameUIPaintRevision();
    if ( game_ui_revision !== this.last_game_ui_revision )
    {
      this.last_game_ui_revision = game_ui_revision;
      this.ui_dirty = true;
    }

    if ( this.booster_bump_started_at !== null )
      this.ui_dirty = true;

    if ( this.params?.aim_tutorial_target )
      this.ui_dirty = true;

    return this.ui_dirty;
  }

  private static drawUIHighlights(): void
  {
    if ( !this.params || !this.text_ctx || !this.text_canvas )
      return;

    const destination_bounds = this.text_canvas.getBoundingClientRect();
    const bump_scale = this.getBoosterBumpScale( performance.now() );
    for ( const highlight of this.params.ui_highlights )
    {
      const source_bounds = highlight.canvas.getBoundingClientRect();
      const rect = highlight.client_rect;
      const sx = ( rect.x - source_bounds.left ) / source_bounds.width * highlight.canvas.width;
      const sy = ( rect.y - source_bounds.top ) / source_bounds.height * highlight.canvas.height;
      const sw = rect.width / source_bounds.width * highlight.canvas.width;
      const sh = rect.height / source_bounds.height * highlight.canvas.height;
      const base_dx = ( rect.x - destination_bounds.left ) / destination_bounds.width * Game.screenW();
      const base_dy = ( rect.y - destination_bounds.top ) / destination_bounds.height * Game.screenH();
      const base_dw = rect.width / destination_bounds.width * Game.screenW();
      const base_dh = rect.height / destination_bounds.height * Game.screenH();
      const dw = base_dw * bump_scale;
      const dh = base_dh * bump_scale;
      const dx = base_dx - ( dw - base_dw ) / 2;
      const dy = base_dy - ( dh - base_dh ) / 2;

      this.text_ctx.drawImage( highlight.canvas, sx, sy, sw, sh, dx, dy, dw, dh );
    }
  }

  private static getBoosterBumpScale( now: number ): number
  {
    if ( this.booster_bump_started_at === null )
      return 1;

    const progress = THREE.MathUtils.clamp(
      ( now - this.booster_bump_started_at ) / this.BOOSTER_BUMP_DURATION_MS,
      0,
      1
    );
    if ( progress >= 1 )
      return 1;

    return 1 + Math.sin( progress * Math.PI ) * this.BOOSTER_BUMP_SCALE;
  }

  // The closing bump must survive until hide() runs, otherwise the highlight
  // renderer can stay open forever.
  private static updateBoosterBump( now: number ): void
  {
    if ( this.booster_bump_started_at === null || this.hide_after_booster_bump )
      return;

    if ( now - this.booster_bump_started_at < this.BOOSTER_BUMP_DURATION_MS )
      return;

    this.booster_bump_started_at = null;
    this.ui_dirty = true;
  }

  private static renderUI(): void
  {
    if ( !this.params || !this.text_ctx || !this.title || !this.description )
      return;

    this.ui_dirty = false;
    this.diag.ui++;

    this.text_ctx.clearRect( 0, 0, Game.screenW(), Game.screenH() );
    if ( this.isTimed() && this.countdown_started )
      this.drawInfiniteShotsGradient();
    if ( SceneDimmer.isActive() )
      this.drawUIHighlights();

    this.title.forceMakeShouldBeRendered();
    this.description.forceMakeShouldBeRendered();
    this.title.render();
    this.description.render();

    if ( this.params.aim_tutorial_target && this.aim_tutorial_cursor )
    {
      this.updateAimTutorialCursor( performance.now() );
      this.aim_tutorial_cursor.forceMakeShouldBeRendered();
      this.aim_tutorial_cursor.render();
    }
  }

  private static renderProgressBar(): void
  {
    const ctx = this.progress_ctx;
    if ( !ctx )
      return;

    const progress = this.countdown_started ? this.getRemainingShare() : 1;

    ctx.clearRect( 0, 0, this.progress_origin.width, this.progress_origin.height );
    this.drawInfiniteProgressBar( ctx, progress );

    this.diag.progress++;
  }

  private static drawInfiniteProgressBar( ctx: CanvasRenderingContext2D, progress: number ): void
  {
    const { x, y, width, height } = this.progress_bar_rect;
    const fill_inset_x = LayoutScaler.widthFromDesign( this.INFINITE_BAR_FILL_INSET_X );
    const fill_h = LayoutScaler.heightFromDesign( this.INFINITE_BAR_FILL_H );
    const fill_x = x + fill_inset_x;
    const fill_y = y + ( height - fill_h ) / 2;
    const fill_w = ( width - fill_inset_x * 2 ) * progress;

    const freeze = this.isFreeze();
    const base_id = freeze ? ImgID.UI_GameFreezeProgressBase : ImgID.UI_GameBoosterProgressBase;
    const fill_id = freeze ? ImgID.UI_GameFreezeProgressFill : ImgID.UI_GameBoosterProgressFill;

    this.drawPill( ctx, base_id, x, y, width, height );
    if ( fill_w > 0 )
      this.drawPill( ctx, fill_id, fill_x, fill_y, fill_w, fill_h );
  }

  private static drawPill(
    ctx: CanvasRenderingContext2D,
    img_id: ImgID,
    x: number,
    y: number,
    w: number,
    h: number
  ): void
  {
    const img = ImagesHolder.getLoadedImage( img_id );
    if ( !img )
      return;

    const src_cap = img.height / 2;
    const src_mid = img.width - src_cap * 2;
    const cap = Math.min( h / 2, w / 2 );
    const mid = w - cap * 2;

    ctx.drawImage( img, 0, 0, src_cap, img.height, x, y, cap, h );
    if ( mid > 0 )
      ctx.drawImage( img, src_cap, 0, src_mid, img.height, x + cap, y, mid, h );
    ctx.drawImage( img, img.width - src_cap, 0, src_cap, img.height, x + w - cap, y, cap, h );
  }

  private static configureAimTutorialCursor(): void
  {
    if ( !this.aim_tutorial_cursor || !this.text_canvas || !this.params?.aim_tutorial_target )
    {
      this.hideAimTutorialCursor();
      return;
    }

    if ( this.hide_after_booster_bump
      && this.booster_bump_started_at !== null
      && performance.now() - this.booster_bump_started_at >= this.BOOSTER_BUMP_DURATION_MS )
    {
      this.hide();
      return;
    }

    const bounds = this.text_canvas.getBoundingClientRect();
    const target = this.params.aim_tutorial_target;
    this.aim_cursor_rest_pos = new Vector2(
      ( target.client_x - bounds.left ) / bounds.width * Game.screenW(),
      ( target.client_y - bounds.top ) / bounds.height * Game.screenH()
    );

    const cursor = this.aim_tutorial_cursor;
    cursor.stopAllTransforms();
    cursor.setScaleImmediate( new Vector2( 1, 1 ) );
    cursor.setPixelPos( this.aim_cursor_rest_pos, true );
    cursor.setOpacityImmediate( 1 );
  }

  private static updateAimTutorialCursor( now: number ): void
  {
    if ( !this.aim_tutorial_cursor || !this.aim_cursor_rest_pos )
      return;

    const cycle = ( now % ( this.CURSOR_PULSE_DURATION_MS * 2 ) )
      / this.CURSOR_PULSE_DURATION_MS;
    const linear = cycle <= 1 ? cycle : 2 - cycle;
    const eased = linear < 0.5
      ? 2 * linear * linear
      : 1 - Math.pow( -2 * linear + 2, 2 ) / 2;
    const scale = 1 - ( 1 - this.CURSOR_PULSE_SCALE ) * eased;
    const rect = this.aim_tutorial_cursor.getRect();

    this.aim_tutorial_cursor.setScaleImmediate( new Vector2( scale, scale ) );
    this.aim_tutorial_cursor.setPixelPos( new Vector2(
      this.aim_cursor_rest_pos.x - ( 1 - scale ) * rect.getW2(),
      this.aim_cursor_rest_pos.y - ( 1 - scale ) * rect.getH2()
    ), true );
  }

  private static hideAimTutorialCursor(): void
  {
    if ( !this.aim_tutorial_cursor )
      return;

    this.aim_tutorial_cursor.stopAllTransforms();
    this.aim_tutorial_cursor.setScaleImmediate( new Vector2( 1, 1 ) );
    if ( this.aim_cursor_rest_pos )
      this.aim_tutorial_cursor.setPixelPos( this.aim_cursor_rest_pos, true );
    this.aim_tutorial_cursor.setOpacityImmediate( 0 );
    this.aim_cursor_rest_pos = null;
  }

  private static gradient_cache: CanvasGradient | null = null;
  private static gradient_cache_bottom = -1;

  private static drawInfiniteShotsGradient(): void
  {
    if ( !this.text_ctx )
      return;

    const gradient_bottom = Game.safeAreaTop()
      + LayoutScaler.heightFromDesign( this.INFINITE_GRADIENT_HEIGHT );

    if ( !this.gradient_cache || this.gradient_cache_bottom !== gradient_bottom )
    {
      const gradient = this.text_ctx.createLinearGradient( 0, 0, 0, gradient_bottom );
      gradient.addColorStop( 0, `rgba(0, 0, 0, ${this.INFINITE_GRADIENT_OPACITY})` );
      gradient.addColorStop( 0.45, "rgba(0, 0, 0, 0.5)" );
      gradient.addColorStop( 1, "rgba(0, 0, 0, 0)" );
      this.gradient_cache = gradient;
      this.gradient_cache_bottom = gradient_bottom;
    }

    this.text_ctx.save();
    this.text_ctx.fillStyle = this.gradient_cache;
    this.text_ctx.fillRect( 0, 0, Game.screenW(), gradient_bottom );
    this.text_ctx.restore();
  }

  private static isFreeze(): boolean
  {
    return this.params?.freeze_share !== undefined;
  }

  private static isTimed(): boolean
  {
    return this.isFreeze() || this.params?.booster_type === BuyBoosterType.InfiniteShots;
  }

  private static getRemainingShare(): number
  {
    const share = this.params?.freeze_share
      ? this.params.freeze_share()
      : 1 - ( performance.now() - this.duration_started_at ) / this.getInfiniteShotsDurationMs();

    return THREE.MathUtils.clamp( share, 0, 1 );
  }

  private static usesFullSceneDimming(): boolean
  {
    return !this.isTimed() || !this.countdown_started;
  }

  private static usesProgressBar(): boolean
  {
    return this.isTimed() && this.progress_ctx !== null;
  }

  private static syncDimmingMode(): void
  {
    const use_full_scene_dimming = this.usesFullSceneDimming();
    if ( use_full_scene_dimming === SceneDimmer.isActive() )
      return;

    this.params?.on_scene_dimming( use_full_scene_dimming );
    if ( !use_full_scene_dimming )
      SceneDimmer.disable();

    this.ui_dirty = true;
  }

  private static syncLayout(): void
  {
    if ( !this.text_canvas || !this.text_ctx )
      return;

    const source = Renderer.renderer.domElement;
    const width = Number.parseFloat( source.style.width ) || Game.screenW();
    const height = Number.parseFloat( source.style.height ) || Game.screenH();
    const pixel_width = Math.round( width * Game.getDpr() );
    const pixel_height = Math.round( height * Game.getDpr() );

    if ( this.text_canvas.width !== pixel_width || this.text_canvas.height !== pixel_height )
    {
      this.text_canvas.width = pixel_width;
      this.text_canvas.height = pixel_height;
      this.ui_dirty = true;
    }

    const layout_key = `${source.style.left}|${source.style.top}|${source.style.width}|${source.style.height}`;
    if ( layout_key !== this.last_layout_key )
    {
      this.last_layout_key = layout_key;
      this.ui_dirty = true;
    }
    this.text_ctx.setTransform( Game.getDpr(), 0, 0, Game.getDpr(), 0, 0 );
    this.text_canvas.style.left = source.style.left;
    this.text_canvas.style.top = source.style.top;
    this.text_canvas.style.width = source.style.width;
    this.text_canvas.style.height = source.style.height;

    this.syncProgressLayout( source, width, height );
  }

  private static syncProgressLayout(
    source: HTMLCanvasElement,
    css_width: number,
    css_height: number
  ): void
  {
    if ( !this.progress_canvas || !this.progress_ctx )
      return;

    const scale_x = css_width / Game.screenW();
    const scale_y = css_height / Game.screenH();
    const dpr = Game.getDpr();

    const pixel_width = Math.round( this.progress_origin.width * scale_x * dpr );
    const pixel_height = Math.round( this.progress_origin.height * scale_y * dpr );

    if ( this.progress_canvas.width !== pixel_width || this.progress_canvas.height !== pixel_height )
    {
      this.progress_canvas.width = pixel_width;
      this.progress_canvas.height = pixel_height;
    }

    this.progress_ctx.setTransform( scale_x * dpr, 0, 0, scale_y * dpr, 0, 0 );

    const left = Number.parseFloat( source.style.left ) || 0;
    const top = Number.parseFloat( source.style.top ) || 0;
    this.progress_canvas.style.left = `${left + this.progress_origin.x * scale_x}px`;
    this.progress_canvas.style.top = `${top + this.progress_origin.y * scale_y}px`;
    this.progress_canvas.style.width = `${this.progress_origin.width * scale_x}px`;
    this.progress_canvas.style.height = `${this.progress_origin.height * scale_y}px`;
  }
}
